import React, { useEffect, useState } from "react";
import { Col, Input, Row, Select, Form, DatePicker, Button } from "antd";
import { ReactComponent as SaveIcon } from "../../../assets/save-icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  createProject,
  setProjectDetailsIsEdit,
  updateProject,
} from "../redux/actions";
import { fetchAllLocations } from "../../LocationManager/redux/actions";
import SpinnerComponent from "../../../components/spinner";
import { isValidNumber } from "../../../globalUtils/axiosConfig";
import moment from "moment";
import AttachmentsSection from "./attachmentsSection";
import ProjectWeightsSection from "./projectWeightsSection";
import ProjectPrioritiesSection from "./projectPrioritiesSection";
import DeliveryNotesRecipientsSection from "./deliveryNotesRecipientsSection";
import { size } from "lodash";

const ProjectDetailsSection = ({ isProjectUpdate, canUpdate }) => {
  const [documents, setDocuments] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [utils, setUtils] = useState({});
  const [currentProject, setCurrentProject] = useState({});

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { TextArea } = Input;
  const { Option } = Select;

  const {
    createProjectLoading,
    currentProjectObj,
    projectDetailsIsEdit,
    utilsData,
  } = useSelector((state) => state.projectsSetupReducer);

  const {
    allProjectStatuses
  } = useSelector(
    (state) => state.projectStatusReducer
  );

  const { allLocations, fetchLocationsLoading } = useSelector(
    (state) => state.locationsReducer
  );
  
  // Get the current user to check permissions
  const { user } = useSelector((state) => state.authReducer);

  useEffect(() => {
    // Fetch all locations for the dropdown
    dispatch(fetchAllLocations({}));
  }, []);

  useEffect(() => {
    if (currentProjectObj) {
      const { endDate, startDate, attachments } = currentProjectObj;

      // Prepare a version of project data where location is an ID
      let projectDataForForm = { ...currentProjectObj };
      if (projectDataForForm.location && typeof projectDataForForm.location === 'object') {
        projectDataForForm.location = projectDataForForm.location.id; // Ensure location is an ID
      } // If it's already a primitive ID, it's fine.

      setCurrentProject(projectDataForForm); // Use this for initialValues via currentProject state
      
      form.setFieldsValue({
        ...projectDataForForm, // This now also has location as an ID
        endDate: endDate ? moment(endDate) : undefined, // Handle potentially undefined dates
        startDate: startDate ? moment(startDate) : undefined,
      });

      if (attachments && attachments?.length >= 0) {
        let updatedImages = attachments.map((img) => {
          let { id, path, originalName } = img;
          console.log("name", originalName);

          if (!originalName && path) {
            const urlParts = path.split("/");
            originalName = urlParts[urlParts.length - 1].split("?")[0];
            try {
              originalName = decodeURIComponent(originalName);
            } catch (e) {
              console.error("Error decoding URI:", e);
            }
            
            originalName = originalName.replaceAll(/[^a-zA-Z0-9.-\s]/g, " ").trim();
          }
          
          return { key: id, image: path, isLocal: false, name: originalName };
        });

        setDocuments(updatedImages);
      }
      setUtils(utilsData);
    } else {
      setCurrentProject({});
      form.resetFields();
    }
  }, [currentProjectObj, form, dispatch, utilsData]);

  useEffect(() => {
    setLoading(createProjectLoading);
    // if (createProjectLoading) {
    //   setIsUserImgSelected(false);
    // }
  }, [createProjectLoading]);

  useEffect(() => {
    if (!isProjectUpdate) {
      // For new projects, start in edit mode
      setIsEdit(true);
      dispatch(setProjectDetailsIsEdit(true));
    } else {
      // For existing projects, start in view mode
      setIsEdit(false);
      dispatch(setProjectDetailsIsEdit(false));
    }
  }, [isProjectUpdate, dispatch]);

  const onChange = (value) => {
    // console.log(`selected ${value}`);
  };

  const onFinish = async (values) => {
    const { location, ...otherValues } = values; // Separate location

    const payload = {
      ...otherValues, // Spread other values
      ...(documents && { documents }), // Add documents if they exist
    };

    if (location !== undefined && location !== null && location !== '') { // Ensure location has a value
      const numericLocationId = parseInt(location, 10); // Convert to number
      if (!isNaN(numericLocationId)) { // Check if conversion was successful
        payload.locationId = numericLocationId;
      } else {
        // Handle error: location ID from form is not a valid number
        console.error("Location ID is not a valid number:", location);
        // Optionally, you might want to prevent form submission or show an error to the user here
        return; 
      }
    }
    // The original 'location' property (from form values) is now excluded

    if (isProjectUpdate) {
      payload.id = currentProjectObj.id; // Add id for update
      dispatch(updateProject(payload));
    } else {
      dispatch(createProject(payload));
    }
  };

  const formItemLayout = {
    labelCol: {
      span: 24,
    },
    wrapperCol: {
      span: 24,
    },
  };

  const handleSave = () => {
    form.submit();
    // After form submission, switch back to view mode
    setIsEdit(false);
    dispatch(setProjectDetailsIsEdit(false));
  };

  const handleEdit = () => {
    // Switch to edit mode
    setIsEdit(true);
    dispatch(setProjectDetailsIsEdit(true));
  };

  // const capitalizeFirstLetter = (str) =>
  //   str && str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <>
      <Row>
        <Col span={24} className="heading-col">
          <Row align="middle" justify="space-between">
            <Col>
              <div>
                <span className="heading-span">Project Details</span>
              </div>
            </Col>
            <Col>
              {isEdit ? (
                loading ? (
                  <SpinnerComponent fontSize={14} />
                ) : (
                  <SaveIcon width={16} height={16} fill="#f25003" onClick={handleSave} />
                )
              ) : (
                canUpdate && (
                  <EditIcon
                    width={16}
                    fill="#f25003"
                    height={16}
                    onClick={handleEdit}
                  />
                )
              )}
            </Col>
          </Row>
        </Col>
        <Col
          span={24}
          className={`user-details-section project-details-section ${
            !isEdit ? "view-mode" : "edit-mode"
          }`}
        >
          <Row justify="center">
            <Col md={24} lg={23} xl={20} xxl={16}>
              <Form
                {...formItemLayout}
                form={form}
                name="user-details"
                onFinish={onFinish}
                initialValues={currentProject}
                scrollToFirstError
                labelWrap={true}
                layout="horizontal"
                className="user-detail-form"
                requiredMark={false}
              >
                <Row gutter={[20, 10]}>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="projectNumber"
                      label="PROJECT NUMBER"
                    >
                      <Input
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate}
                        placeholder="Project Number"
                      />
                    </Form.Item>
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required",
                        },
                      ]}
                      name="proposalNumber"
                      label="Proposal Number"
                    >
                      <Input
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate}
                        placeholder="Proposal Number"
                      />
                    </Form.Item>
                  </Col>
                  {/* <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                        { pattern: isValidNumber, message: "Invalid" },
                      ]}
                      name="document_numbering_abbreviation"
                      label="Document Numbering Abbreviation"
                    >
                      <Input
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate}
                        placeholder="Document Numbering Abbreviation"
                      />
                    </Form.Item>
                  </Col> */}
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="client"
                      label="Client"
                    >
                      <Input
                        readOnly={loading}
                        disabled={
                          !isEdit ||
                          // isProjectUpdate ||
                          !canUpdate
                        }
                        placeholder="Client"
                      />
                    </Form.Item>
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="contactName"
                      label="Contact Name"
                    >
                      <Input
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate}
                        placeholder="Contact Name"
                      />
                    </Form.Item>
                  </Col>

                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                        {
                          pattern: /^[0-9+-\s]+$/,
                          message: "Please enter a valid phone number!",
                        },
                      ]}
                      name="contactNumber"
                      label="Contact Number"
                    >
                      <Input
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate}
                        placeholder="Contact Number"
                      />
                    </Form.Item>
                  </Col>

                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                        {
                          type: 'email',
                          message: 'Please enter a valid email!',
                        },
                      ]}
                      name="contactEmail"
                      label="Contact Email"
                    >
                      <Input
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate}
                        placeholder="Contact Email"
                      />
                    </Form.Item>
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="projectAddress"
                      label="Project Address"
                    >
                      <Input
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate}
                        placeholder="Project Address"
                      />
                    </Form.Item>
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="location"
                      label="Location"
                    >
                      <Select
                        style={{
                          width: "100%",
                        }}
                        disabled={!isEdit || !canUpdate}
                        loading={fetchLocationsLoading}
                        className="location-select"
                        dropdownClassName="select-dropdown-custom"
                        getPopupContainer={() =>
                          document.getElementsByClassName(
                            "ant-layout-content layout-content site-layout-background custom-textarea"
                          )[0]
                        }
                        showSearch
                        placeholder="Select Location"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {allLocations.map(({ id, name }) => {
                          return (
                            <Option key={id} value={id}>
                              {name}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      name="contractNumber"
                      label="Contract Number"
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                    >
                      <Input
                        readOnly={loading}
                        disabled={!isEdit} // || isProjectUpdate
                        placeholder="Contract Number"
                      />
                    </Form.Item>
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="typeOfProject"
                      label="Type of Project"
                    >
                      <Input
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate}
                        placeholder="Type of Project"
                      />
                    </Form.Item>
                  </Col>

                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="startDate"
                      label="Start Date"
                    >
                      <DatePicker
                        style={{
                          width: "100%",
                        }}
                        format="D-MM-YYYY"
                        allowClear={false}
                        suffixIcon={null}
                        placeholder="Select Start Date"
                        disabled={!isEdit || !canUpdate || loading}
                      />
                    </Form.Item>
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      name="endDate"
                      label="End Date"
                      dependencies={["startDate"]}
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (
                              !value ||
                              moment(
                                getFieldValue("startDate")
                              ).isSameOrBefore(value)
                            ) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error("End date should be after start date!")
                            );
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        suffixIcon={null}
                        style={{
                          width: "100%",
                        }}
                        format="D-MM-YYYY"
                        allowClear={false}
                        placeholder="Select End Date"
                        disabled={!isEdit || !canUpdate || loading}
                      />
                    </Form.Item>
                  </Col>
                  <Col md={8} lg={8} xl={8} xxl={8}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="projectStatusId"
                      label="Project Status"
                    >
                      <Select
                        style={{
                          width: "100%",
                        }}
                        disabled={!isEdit || !canUpdate}
                        readOnly={true}
                        className="department-select"
                        dropdownClassName="select-dropdown-custom"
                        getPopupContainer={() =>
                          document.getElementsByClassName(
                            "ant-layout-content layout-content site-layout-background custom-textarea"
                          )[0]
                        }
                        showSearch
                        placeholder="Select Project Status"
                        optionFilterProp="children"
                        onChange={onChange}
                        // onSearch={onSearch}
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {allProjectStatuses.map(({ id, statusName }) => {
                          return (
                            <Option key={id} value={id}>
                              {statusName}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Required!",
                        },
                      ]}
                      name="projectScope"
                      label="Project Scope"
                    >
                      <TextArea
                        className="scope"
                        rows={7}
                        readOnly={loading}
                        disabled={!isEdit || !canUpdate}
                        placeholder="Insert Project Scope"
                      />
                    </Form.Item>
                    <AttachmentsSection
                      isEdit={isEdit}
                      loading={loading}
                      documents={documents}
                      setDocuments={setDocuments}
                      currentProject={currentProject}
                    />
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="empty-div"></div>
      
      {/* Project Weights Section - Only show if user has permission */}
      {user?.permissions?.project_weightings?.view && (
        <ProjectWeightsSection 
          isProjectUpdate={isProjectUpdate} 
          canUpdate={canUpdate && user?.permissions?.project_weightings?.add_edit} 
        />
      )}
      
      {/* Project Priorities Section - Only show if user has permission */}
      {user?.permissions?.project_priorities?.view && (
      <ProjectPrioritiesSection 
        isProjectUpdate={isProjectUpdate} 
        canUpdate={canUpdate && user?.permissions?.project_priorities?.add_edit} 
      />
      )}
      
      {/* Delivery Notes Notification Recipients Section */}
      <DeliveryNotesRecipientsSection 
        isProjectUpdate={isProjectUpdate} 
        canUpdate={canUpdate}
        projectId={currentProjectObj?.id}
        initialRecipients={currentProjectObj?.deliveryNotesNotificationsRecipients || []}
      />
      
      {/* Navisworks Integration File Export Section - Only show if user has permission */}
      {user?.permissions?.navisworks_file_export?.export && (
        <>
          <Row>
            <Col span={24} className="heading-col">
              <Row align="middle" justify="space-between">
                <Col>
                  <div>
                    <span className="heading-span">Navisworks Integration File Export</span>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col
              span={24}
              className={`user-details-section project-details-section ${
                !isEdit ? "view-mode" : "edit-mode"
              }`}
            >
              <Row justify="center">
                <Col md={24} lg={23} xl={20} xxl={16}>
                  <Form
                    {...formItemLayout}
                    form={form}
                    name="navisworks-url"
                    onFinish={onFinish}
                    initialValues={currentProject}
                    scrollToFirstError
                    labelWrap={true}
                    layout="horizontal"
                    className="user-detail-form"
                    requiredMark={false}
                  >
                    <Row gutter={[20, 10]}>
                      <Col span={24}>
                      <Button
                        style={{
                          backgroundColor: "#f25003",
                          color: "white",
                          width: "250px",
                          height: "30px",
                          fontFamily: "Open Sans, Arial, sans-serif",
                          fontWeight: "600",
                          fontSize: "13px",
                        }}
                      >
                        Export File For Navisworks
                      </Button>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>
            </Col>
          </Row>
          <div className="empty-div"></div>
        </>
      )}
    </>
  );
};

export default ProjectDetailsSection;
